import { Link } from "react-router-dom";
import { useLocale } from "@/contexts/LanguageContext";
import { navigation, labels, footer as footerContent, offices, practiceAreas } from "@/data/content";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
const martinezLogo = "/martinez-logo-sm.webp";

export function Footer() {
  const { locale, t, getLocalizedHref } = useLocale();

  return (
    <footer className="bg-secondary/50 border-t border-border/30" role="contentinfo">
      <div className="container mx-auto max-w-7xl px-6 md:px-8 lg:px-10 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-14">
          {/* Column 1: Brand */}
          <div>
            <Link to={locale === "es" ? "/es" : "/"} className="inline-block mb-6">
              <img src={martinezLogo} alt="Martinez Law" className="h-16 w-auto" width="250" height="112" />
            </Link>
            <p className="text-base text-muted-foreground mb-7">{t(footerContent.tagline)}</p>
            <div className="space-y-3.5">
              <a href="tel:+17738183020" className="flex items-center gap-3 text-base text-muted-foreground hover:text-foreground transition-colors">
                <Phone className="h-4.5 w-4.5 text-primary/60" />
                (773) 818-3020
              </a>
              <a href="mailto:info@martinezlawllc.net" className="flex items-center gap-3 text-base text-muted-foreground hover:text-foreground transition-colors">
                <Mail className="h-4.5 w-4.5 text-primary/60" />
                info@martinezlawllc.net
              </a>
              <a href="https://wa.me/17738183020" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-base text-muted-foreground hover:text-foreground transition-colors">
                <MessageCircle className="h-4.5 w-4.5 text-primary/60" />
                WhatsApp
              </a>
            </div>
            <p className="text-base text-muted-foreground mt-6 italic">
              {locale === "es"
                ? "Con cita · Noches y fines de semana disponibles para asuntos urgentes"
                : "By appointment · Evenings & weekends available for urgent matters"}
            </p>
          </div>

          {/* Column 2: Practice Areas */}
          <div>
            <h3 className="micro-label text-foreground mb-6">
              {locale === "es" ? "Áreas de Práctica" : "Practice Areas"}
            </h3>
            <ul className="space-y-3.5">
              {practiceAreas.slice(0, 6).map((area) => (
                <li key={area.id}>
                  <Link to={getLocalizedHref(area.href, area.esHref)} className="text-base text-muted-foreground hover:text-foreground transition-colors">
                    {t(area.title)}
                  </Link>
                </li>
              ))}
              <li>
                <Link to={getLocalizedHref("/practice-areas")} className="text-base text-primary hover:text-foreground transition-colors font-medium">
                  {t(labels.viewAll)} →
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Locations */}
          <div>
            <h3 className="micro-label text-foreground mb-6">
              {locale === "es" ? "Oficinas" : "Offices"}
            </h3>
            <ul className="space-y-6">
              {offices.map((office, i) => (
                <li key={i} className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary/60 mt-1 shrink-0" />
                  <div>
                    <p className="text-base font-medium text-foreground">{t(office.city)}</p>
                    <p className="text-base text-muted-foreground">{office.address}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Quick Links */}
          <div>
            <h3 className="micro-label text-foreground mb-6">
              {locale === "es" ? "Enlaces Rápidos" : "Quick Links"}
            </h3>
            <ul className="space-y-3.5">
              {navigation.filter(n => !n.children && n.href !== "/").map((item) => (
                <li key={item.href}>
                  <Link to={getLocalizedHref(item.href, item.esHref)} className="text-base text-muted-foreground hover:text-foreground transition-colors">
                    {t(item.label)}
                  </Link>
                </li>
              ))}
              <li>
                <Link to={getLocalizedHref("/results", "/es/resultados")} className="text-base text-muted-foreground hover:text-foreground transition-colors">
                  {locale === "es" ? "Resultados" : "Results"}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedHref("/attorneys", "/es/abogados")} className="text-base text-muted-foreground hover:text-foreground transition-colors">
                  {locale === "es" ? "Abogados" : "Attorneys"}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedHref("/start-here", "/es/comience-aqui")} className="text-base text-muted-foreground hover:text-foreground transition-colors">
                  {locale === "es" ? "Comience Aquí" : "Start Here"}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedHref("/emergency", "/es/emergencia")} className="text-base text-primary hover:text-foreground transition-colors font-medium">
                  {locale === "es" ? "Ayuda de Emergencia" : "Emergency Help"}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedHref("/client-portal")} className="text-base text-primary hover:text-foreground transition-colors font-medium">
                  {t(labels.clientPortal)}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border/30">
        <div className="container mx-auto max-w-7xl px-6 md:px-8 lg:px-10 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            <p className="text-base text-muted-foreground">{t(footerContent.copyright)}</p>
            <div className="flex items-center gap-6">
              <Link to={getLocalizedHref("/privacy", "/es/privacidad")} className="text-base text-muted-foreground hover:text-foreground transition-colors">{t(footerContent.links.privacy)}</Link>
              <Link to={getLocalizedHref("/terms", "/es/terminos")} className="text-base text-muted-foreground hover:text-foreground transition-colors">{t(footerContent.links.terms)}</Link>
              <Link to={getLocalizedHref("/accessibility")} className="text-base text-muted-foreground hover:text-foreground transition-colors">{t(footerContent.links.accessibility)}</Link>
            </div>
          </div>
          <p className="text-base text-muted-foreground mt-6 max-w-3xl">{t(footerContent.disclaimer)}</p>
        </div>
      </div>
    </footer>
  );
}
